import React, { useCallback, type FC } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY,
  Button,
  Flex,
  FormElement,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Select,
  Text,
} from 'mailup-web-components'

import { INPUT_MESSAGES_MAX_ITEMS } from 'Constants'

import useAsyncSelect from 'Hooks/useAsyncSelect/useAsyncSelect'

import {
  modalPendingCancelCallback,
  modalPendingChangeMessageCallback,
  modalPendingConfirmCallback,
} from 'Pages/RecipientsList/recoil/callbacks/modalPending'
import {
  modalPendingHasMessageFeedback,
  modalPendingState,
  modalPendingSubmitDisabledState,
} from 'Pages/RecipientsList/recoil/state/modalPending'

import {
  fetchMessageById,
  fetchMessages,
} from 'Pages/RecipientsList/recoil/services/asyncSelectConfirmationMessages'

const ModalPending: FC = () => {
  const intl = useIntl()

  const { show, idConfirmationMessage } = useRecoilValue(modalPendingState)

  const showFeedback = useRecoilValue(modalPendingHasMessageFeedback)

  const submitDisabled = useRecoilValue(modalPendingSubmitDisabledState)

  const handleChangeMessage = useRecoilCallback(
    modalPendingChangeMessageCallback,
    [],
  )

  const { options, loading, handleSearch, hint, hintNoResults } =
    useAsyncSelect({
      key: 'modal-pending',
      value: idConfirmationMessage,
      maxItems: INPUT_MESSAGES_MAX_ITEMS,
      fetchItems: async () => await fetchMessages(''),
      fetchItemsByTerm: fetchMessages,
      fetchItemById: fetchMessageById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const handleCancel = useRecoilCallback(modalPendingCancelCallback, [])
  const modalConfirm = useRecoilCallback(modalPendingConfirmCallback, [])

  const confirmWithParam = useCallback(
    (p: boolean) => {
      return (): void => {
        void modalConfirm(p)
      }
    },
    [modalConfirm],
  )

  return (
    <Modal
      show={show}
      onClose={handleCancel}
      closeOnOverlayClick
      data-mu-test="modal-pending"
    >
      <ModalHead
        title={intl.formatMessage({
          id: 'Load.RecipientsList.ModalPendingTitle',
        })}
      />
      <ModalBody>
        <Flex flexDirection="column" gap="24px">
          <Text>
            {intl.formatMessage({
              id: 'Load.RecipientsList.ModalPendingIntro',
            })}
          </Text>

          <FormElement
            required
            label={intl.formatMessage({
              id: 'Load.RecipientsList.ModalPendingInputMessage',
            })}
            variant={showFeedback ? 'danger' : undefined}
            feedback={
              showFeedback
                ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
                : undefined
            }
          >
            <Select
              data-mu-test="modal-pending-input-message"
              value={idConfirmationMessage}
              options={options}
              loading={loading}
              onChange={handleChangeMessage}
              onSearch={handleSearch}
              hint={hint}
              hintNoResults={hintNoResults}
              memoSelectOption
              error={showFeedback}
              placeholder={intl.formatMessage({
                id: 'Load.RecipientsList.ModalPendingSelectMessage',
              })}
            />
          </FormElement>
        </Flex>
      </ModalBody>
      <ModalFoot>
        <Button
          data-mu-test="confirm-pending"
          disabled={submitDisabled}
          kind={BUTTON_VARIANT_SECONDARY}
          onClick={confirmWithParam(false)}
        >
          {intl.formatMessage({
            id: 'Load.RecipientsList.ModalPendingConfirm',
          })}
        </Button>
        <Button
          data-mu-test="confirm-pending-send-confirmation"
          disabled={submitDisabled}
          kind={BUTTON_VARIANT_PRIMARY}
          onClick={confirmWithParam(true)}
        >
          {intl.formatMessage({
            id: 'Load.RecipientsList.ModalPendingConfirmSendConfirmation',
          })}
        </Button>
      </ModalFoot>
    </Modal>
  )
}

export default ModalPending
